import { c as _c } from "react/compiler-runtime";
import esriConfig from "@arcgis/core/config";
import Basemap from "@arcgis/core/Basemap";
import { runtimeConfig } from "@src/runtime-config";
import "@arcgis/map-components/components/arcgis-map";
import "@arcgis/map-components/components/arcgis-scene";
import { useMapInitialization } from "@components/world-map/hooks/use-map-initialization";
import { useMapAttribution } from "@components/world-map/hooks/use-map-attribution";
import { useMapOrbit } from "@components/world-map/hooks/use-map-orbit";
import { useMapViewSync } from "@components/world-map/hooks/use-map-view-sync";
import { useMapWheelZoom } from "@components/world-map/hooks/use-map-wheel-zoom";
import { useMarker3DLift } from "@components/world-map/hooks/use-marker-3d-lift";
import { useMarkerPulseOverlay } from "@components/world-map/hooks/use-marker-pulse-overlay";
import { useVisibleEntities } from "@components/world-map/hooks/use-visible-entities";
import { useWorldMapHighlight } from "@components/world-map/hooks/use-world-map-highlight";
import { useWorldMapPopups } from "@components/world-map/hooks/use-world-map-popups";
import { WorldMapControls } from "@components/world-map/world-map-controls";
import { MarkerPulseRing } from "@components/world-map/world-map-marker-pulse";
import { isWorldMap2DModeEnabled, MAP_MARKER_HIGHLIGHT_COLOR, WORLD_MAP_DEFAULT_FIELDS, WORLD_MAP_FOCUS_ZOOM, WORLD_MAP_GOTO_ANIMATION_MAX_DISTANCE_KM, WORLD_MAP_GOTO_ANIMATION_MAX_DURATION_MS } from "@components/world-map/world-map-constants";
import { getGreatCircleDistanceKm, getHorizontalTranslate, getVerticalTranslate, runGoTo } from "@components/world-map/world-map-utils";
import { Box } from "@mui-wrapper";
import { useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { worldMapModeSelector, worldMapStyleSelector } from "@store/ui/ui-selector";
import { setWorldMapMode, setWorldMapStyle } from "@store/ui/ui-slice";

// Authenticate with ArcGIS services using the API key on app initialization.
// This ensures that all imports from @arcgis/core will include the key.
esriConfig.apiKey = runtimeConfig.features.arcgisApiKey;

/**
 * World map that can display entities on a geographical map.
 */
export function WorldMap(t0) {
  const $ = _c(51);
  const {
    entities,
    mapContext,
    onVisibleEntitiesChange,
    fields: t1,
    buttons,
    shouldShowMapControls: t2,
    sx,
    mapRef,
    renderPopupContent,
    onEntityHover,
    onClusterHover,
    onMapClick,
    initialView,
    onViewStable,
    onResetView,
    onMapReady
  } = t0;
  const fields = t1 === undefined ? WORLD_MAP_DEFAULT_FIELDS : t1;
  const shouldShowMapControls = t2 === undefined ? false : t2;
  const dispatch = useAppDispatch();
  const mapStyle = useAppSelector(worldMapStyleSelector);
  const mapMode = useAppSelector(worldMapModeSelector);
  const is3DEnabled = isWorldMap2DModeEnabled ? mapMode === "3D" : true;
  const shouldEnablePopups = Boolean(renderPopupContent);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Basemap();
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const initialBasemap = t3;
  let t4;
  if ($[1] !== entities || $[2] !== fields || $[3] !== initialView || $[4] !== is3DEnabled || $[5] !== mapStyle) {
    t4 = {
      entities,
      fields,
      mapStyle,
      is3DEnabled,
      initialView
    };
    $[1] = entities;
    $[2] = fields;
    $[3] = initialView;
    $[4] = is3DEnabled;
    $[5] = mapStyle;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    viewRef,
    layerRef,
    isViewReady,
    layerVersion,
    handleViewReady,
    consumeSkipNextViewSync
  } = useMapInitialization(t4);
  useMapViewSync(viewRef, isViewReady, onViewStable, consumeSkipNextViewSync);
  const hasReportedMapReadyRef = useRef(false);
  let t5;
  let t6;
  if ($[7] !== isViewReady || $[8] !== onMapReady) {
    t5 = () => {
      if (isViewReady && !hasReportedMapReadyRef.current) {
        hasReportedMapReadyRef.current = true;
        onMapReady?.();
      }
    };
    t6 = [isViewReady, onMapReady];
    $[7] = isViewReady;
    $[8] = onMapReady;
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  useEffect(t5, t6);
  useMapAttribution(viewRef, isViewReady);
  useMapWheelZoom(viewRef, isViewReady);
  useMapOrbit(viewRef, isViewReady);
  const isMarkerLifted = useMarker3DLift(viewRef, layerRef, isViewReady, layerVersion);
  let t7;
  if ($[11] !== entities || $[12] !== isViewReady || $[13] !== layerRef || $[14] !== mapContext || $[15] !== onMapClick || $[16] !== shouldEnablePopups || $[17] !== viewRef) {
    t7 = {
      viewRef,
      layerRef,
      isViewReady,
      entities,
      mapContext,
      isPopupEnabled: shouldEnablePopups,
      onMapClick
    };
    $[11] = entities;
    $[12] = isViewReady;
    $[13] = layerRef;
    $[14] = mapContext;
    $[15] = onMapClick;
    $[16] = shouldEnablePopups;
    $[17] = viewRef;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const {
    selectedMarkerPopup
  } = useWorldMapPopups(t7);
  const {
    viewAllEntities
  } = useVisibleEntities(viewRef, isViewReady, entities, onVisibleEntitiesChange);
  const {
    highlightEntity,
    pulseMapPoint
  } = useWorldMapHighlight(viewRef, layerRef, isViewReady, isMarkerLifted, onEntityHover, selectedMarkerPopup?.entity.id, onClusterHover);
  const pulsePosition = useMarkerPulseOverlay(viewRef, isViewReady, pulseMapPoint);
  let t8;
  let t9;
  if ($[19] !== highlightEntity || $[20] !== viewRef) {
    t8 = () => ({
      goTo: (position, targetZoom) => {
        const view = viewRef.current;
        if (!view) {
          return;
        }
        const target = {
          center: [position.lng, position.lat],
          zoom: targetZoom ?? WORLD_MAP_FOCUS_ZOOM
        };
        const goToOptions = {
          animate: true
        };
        const currentCenter = view.center;
        const currentLong = currentCenter?.longitude;
        const currentLat = currentCenter?.latitude;
        if (typeof currentLong === "number" && typeof currentLat === "number" && getGreatCircleDistanceKm({
          lng: currentLong,
          lat: currentLat
        }, position) >= WORLD_MAP_GOTO_ANIMATION_MAX_DISTANCE_KM) {
          goToOptions.duration = WORLD_MAP_GOTO_ANIMATION_MAX_DURATION_MS;
        }
        runGoTo(view.goTo(target, goToOptions));
      },
      highlightEntity
    });
    t9 = [viewRef, highlightEntity];
    $[19] = highlightEntity;
    $[20] = viewRef;
    $[21] = t8;
    $[22] = t9;
  } else {
    t8 = $[21];
    t9 = $[22];
  }
  useImperativeHandle(mapRef, t8, t9);
  let t10;
  if ($[23] !== sx) {
    t10 = {
      position: "relative",
      width: "100%",
      height: "100%",
      overflow: "hidden",
      ...sx
    };
    $[23] = sx;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== handleViewReady || $[26] !== is3DEnabled) {
    t11 = is3DEnabled ? <arcgis-scene key="world-scene" data-testid="WORLD_MAP" basemap={initialBasemap} ground="world-elevation" onarcgisViewReadyChange={handleViewReady} /> : <arcgis-map key="world-map" data-testid="WORLD_MAP" basemap="gray-vector" onarcgisViewReadyChange={handleViewReady} />;
    $[25] = handleViewReady;
    $[26] = is3DEnabled;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== renderPopupContent || $[29] !== selectedMarkerPopup || $[30] !== shouldEnablePopups) {
    t12 = shouldEnablePopups && selectedMarkerPopup && renderPopupContent && <Box sx={{
      position: "absolute",
      left: selectedMarkerPopup.x,
      top: selectedMarkerPopup.y,
      transform: `translate(${getHorizontalTranslate(selectedMarkerPopup.horizontalPlacement, selectedMarkerPopup.placement)}, ${getVerticalTranslate(selectedMarkerPopup.placement)})`,
      zIndex: 2
    }}>{renderPopupContent(selectedMarkerPopup.entity)}</Box>;
    $[28] = renderPopupContent;
    $[29] = selectedMarkerPopup;
    $[30] = shouldEnablePopups;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== pulsePosition) {
    t13 = pulsePosition && <MarkerPulseRing x={pulsePosition.x} y={pulsePosition.y} color={MAP_MARKER_HIGHLIGHT_COLOR} />;
    $[32] = pulsePosition;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== buttons || $[35] !== dispatch || $[36] !== isViewReady || $[37] !== mapContext || $[38] !== mapMode || $[39] !== mapStyle || $[40] !== onResetView || $[41] !== shouldShowMapControls || $[42] !== viewAllEntities || $[43] !== viewRef) {
    t14 = isViewReady && <WorldMapControls viewRef={viewRef} mapContext={mapContext} shouldShowMapControls={shouldShowMapControls} viewAllEntities={viewAllEntities} mapStyle={mapStyle} setMapStyle={mapStyle_0 => {
      dispatch(setWorldMapStyle(mapStyle_0));
    }} mapMode={mapMode} setMapMode={mapMode_0 => {
      dispatch(setWorldMapMode(mapMode_0));
    }} onResetView={onResetView} externalButtons={buttons} />;
    $[34] = buttons;
    $[35] = dispatch;
    $[36] = isViewReady;
    $[37] = mapContext;
    $[38] = mapMode;
    $[39] = mapStyle;
    $[40] = onResetView;
    $[41] = shouldShowMapControls;
    $[42] = viewAllEntities;
    $[43] = viewRef;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t14) {
    t15 = <Box sx={t10}>{t11}{t12}{t13}{t14}</Box>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  return t15;
}